(() => {
  function planProtection(cart, variantId, available = true) {
    const hasDensea = cart.items.some(item => item.handle === 'densifuse-shampoo' && item.quantity > 0);
    const lines = cart.items.filter(item => String(item.variant_id) === String(variantId));
    const selected = lines.reduce((sum, item) => sum + item.quantity, 0);
    const wanted = hasDensea && cart.attributes?._densea_protection_opt_in === '1' && cart.attributes?._densea_protection_opt_out !== '1' && (available || selected > 0) ? 1 : 0;
    if (selected === wanted && lines.length <= 1) return null;
    if (lines.length > 1) return Object.fromEntries(lines.map((item, index) => [item.key || variantId, index === 0 ? wanted : 0]));
    return { [lines[0]?.key || variantId]: wanted };
  }
  function planUpgrade(cart, singleId, duoId) {
    const items = cart.items.filter(item => item.handle === 'densifuse-shampoo' && item.quantity > 0);
    if (items.length !== 1 || String(items[0].variant_id) !== String(singleId) || items[0].quantity !== 1 || items[0].selling_plan_allocation) return null;
    return { [items[0].key || singleId]: 0, [duoId]: 1 };
  }
  function protectedFormData(source, cart, options) {
    const data = new FormData();
    for (const [key, value] of source.entries()) data.append(key, value);
    const indexed = [...data.keys()].filter(key => /^items\[\d+\]\[id\]$/.test(key));
    const ids = indexed.length ? indexed.map(key => String(data.get(key))) : [String(data.get('id'))];
    const eligible = ids.some(id => options.shampooIds.includes(id));
    const selected = cart.items.some(item => String(item.variant_id) === options.variantId && item.quantity > 0);
    if (!eligible || !options.available || !options.variantId || selected || ids.includes(options.variantId) || cart.attributes?._densea_protection_opt_in !== '1' || cart.attributes?._densea_protection_opt_out === '1') return data;
    let index = 0;
    if (indexed.length) {
      index = Math.max(...indexed.map(key => Number(key.match(/^items\[(\d+)\]/)[1]))) + 1;
    } else {
      for (const [key, value] of [...data.entries()]) {
        if (['id', 'quantity', 'selling_plan'].includes(key)) {
          data.append(`items[0][${key}]`, value);
          data.delete(key);
        } else if (/^properties\[.+\]$/.test(key)) {
          data.append(`items[0][properties]${key.slice(10)}`, value);
          data.delete(key);
        }
      }
      if (!data.has('items[0][quantity]')) data.set('items[0][quantity]', '1');
      index = 1;
    }
    data.set(`items[${index}][id]`, options.variantId);
    data.set(`items[${index}][quantity]`, '1');
    return data;
  }
  function createCartClient(options) {
    let cart = options.initialCart;
    let tail = Promise.resolve();
    const remember = value => { cart = value?.items ? value : null; };
    function serial(task) {
      const result = tail.then(task);
      tail = result.catch(() => {});
      return result;
    }
    async function read() {
      if (cart) return cart;
      const response = await options.fetch(`${options.root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('cart_read');
      remember(await response.json());
      return cart;
    }
    async function update(body) {
      options.busy(true);
      try {
        const response = await options.fetch(`${options.root}cart/update.js`, {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ ...body, sections: options.sections(), sections_url: options.path() })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error('cart_update');
        remember(result);
        return result;
      } catch (error) {
        remember(null);
        throw error;
      } finally { options.busy(false); }
    }
    return {
      remember,
      add(source, config) {
        return serial(async () => {
          const current = await read();
          const body = protectedFormData(source, current, options);
          const requested = new Set(String(body.get('sections') || '').split(',').filter(Boolean));
          ['cart-drawer', 'cart-icon-bubble'].forEach(id => requested.add(id));
          body.set('sections', [...requested].join(','));
          body.set('sections_url', options.path());
          options.busy(true);
          try {
            const response = await options.fetch(`${options.root}cart/add.js`, { ...config, credentials: 'same-origin', body });
            const result = await response.json();
            if (response.ok && !result.status) remember(options.parseState(result.sections?.['cart-drawer']));
            else remember(null);
            return result;
          } catch (error) {
            remember(null);
            throw error;
          } finally { options.busy(false); }
        });
      },
      sync(chosen) {
        return serial(async () => {
          const current = await read();
          const explicit = chosen !== undefined;
          const desired = explicit ? { ...current, attributes: { ...current.attributes, _densea_protection_opt_in: chosen ? '1' : '0', _densea_protection_opt_out: chosen ? '0' : '1' } } : current;
          const updates = planProtection(desired, options.variantId, options.available);
          if (!updates && !explicit) return null;
          const body = { updates: updates || {} };
          if (explicit) body.attributes = { _densea_protection_opt_in: chosen ? '1' : '0', _densea_protection_opt_out: chosen ? '0' : '1' };
          return update(body);
        });
      },
      upgrade(singleId, duoId) {
        return serial(async () => {
          const current = await read();
          const updates = planUpgrade(current, singleId, duoId);
          if (!updates) throw new Error('offer_changed');
          return update({ updates: { ...updates, ...planProtection(current, options.variantId, options.available) } });
        });
      }
    };
  }
  if (typeof module !== 'undefined') module.exports = { planProtection, planUpgrade, protectedFormData, createCartClient };
  if (typeof document === 'undefined') return;
  function start() {
    if (document.documentElement.dataset.denseaCartReady) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer?.dataset.denseaProtectionVariant) return;
    document.documentElement.dataset.denseaCartReady = 'true';
    const isCartPage = () => /\/cart\/?$/.test(location.pathname);
    const relevant = () => drawer.classList.contains('active') || isCartPage();
    let busy = false;
    let timer;
    const parse = text => new DOMParser().parseFromString(text, 'text/html');
    function stateFrom(element) {
      try { return JSON.parse(element?.querySelector('[data-densea-cart-state]')?.textContent || 'null'); } catch { return null; }
    }
    function setBusy(value) {
      busy = value;
      document.querySelectorAll('[data-densea-protection-toggle], [data-densea-cart-upgrade]').forEach(el => { el.disabled = value; });
      document.querySelectorAll('[data-densea-protection]').forEach(el => el.setAttribute('aria-busy', String(value)));
    }
    function sections() {
      const result = ['cart-drawer', 'cart-icon-bubble'];
      ['main-cart-items', 'main-cart-footer'].forEach(id => {
        const sectionId = document.getElementById(id)?.dataset.id;
        if (sectionId) result.push(sectionId);
      });
      return [...new Set(result)];
    }
    const client = createCartClient({
      variantId: drawer.dataset.denseaProtectionVariant,
      available: drawer.dataset.denseaProtectionAvailable === 'true',
      shampooIds: (drawer.dataset.denseaShampooVariants || '').split(','),
      initialCart: stateFrom(drawer),
      root: window.Shopify?.routes?.root || '/',
      fetch: (...args) => fetch(...args),
      busy: setBusy, sections, path: () => location.pathname,
      parseState: html => html ? stateFrom(parse(html)) : null
    });
    window.DenseaCart = client;
    function render(cart, keepFocus) {
      const content = cart.sections || {};
      if (content['cart-drawer']) {
        const next = parse(content['cart-drawer']).querySelector('#CartDrawer .drawer__inner');
        const current = drawer.querySelector('.drawer__inner');
        if (next && current) current.innerHTML = next.innerHTML;
        drawer.classList.toggle('is-empty', cart.item_count === 0);
      }
      if (content['cart-icon-bubble']) {
        const next = parse(content['cart-icon-bubble']).querySelector('.shopify-section');
        const current = document.getElementById('cart-icon-bubble');
        if (next && current) current.innerHTML = next.innerHTML;
      }
      ['main-cart-items', 'main-cart-footer'].forEach(id => {
        const current = document.getElementById(id);
        const source = current?.dataset.id && content[current.dataset.id];
        if (!source) return;
        const next = parse(source).getElementById(id);
        if (next) current.innerHTML = next.innerHTML;
        current.classList.toggle('is-empty', cart.item_count === 0);
      });
      const focusControl = drawer.classList.contains('active') ? drawer.querySelector('[data-densea-protection-toggle]') : document.querySelector('#main-cart-footer [data-densea-protection-toggle]');
      if (drawer.classList.contains('active') && typeof trapFocus === 'function') trapFocus(drawer.querySelector('.drawer__inner'), keepFocus && focusControl ? focusControl : drawer.querySelector('.drawer__close'));
      if (keepFocus) focusControl?.focus({ preventScroll: true });
    }
    async function sync(chosen) {
      if (!relevant()) return;
      try {
        const result = await client.sync(chosen);
        if (result) render(result, chosen !== undefined);
      } catch {
        document.querySelectorAll('[data-protection-error]').forEach(el => { el.hidden = false; });
        if (chosen !== undefined) document.querySelectorAll('[data-densea-protection-toggle]').forEach(el => { el.checked = !chosen; });
      }
    }
    function schedule() { clearTimeout(timer); timer = setTimeout(() => sync(), 80); }
    document.addEventListener('change', event => {
      if (!event.target.matches('[data-densea-protection-toggle]')) return;
      event.stopImmediatePropagation();
      sync(event.target.checked);
    }, true);
    document.addEventListener('click', async event => {
      const button = event.target.closest('[data-densea-cart-upgrade]');
      if (!button) return;
      event.preventDefault();
      if (busy) return;
      const label = button.querySelector('[data-upgrade-label]');
      const original = label?.textContent;
      if (label) label.textContent = button.dataset.busyLabel;
      const source = button.closest('.dn-cart-upgrade');
      try {
        const result = await client.upgrade(button.dataset.singleId, button.dataset.duoId);
        render(result, false);
        const target = drawer.classList.contains('active') ? drawer.querySelector('.cart__checkout-button') : document.querySelector('#main-cart-footer .cart__checkout-button');
        target?.focus({ preventScroll: true });
      } catch {
        const error = source?.querySelector('[data-cart-upgrade-error]');
        if (error) error.hidden = false;
        if (label) label.textContent = original;
      }
    });
    document.addEventListener('click', event => {
      if (!event.target.closest('[data-densea-service] cart-remove-button')) return;
      event.preventDefault(); event.stopImmediatePropagation(); sync(false);
    }, true);
    new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
    window.addEventListener('pageshow', event => { if (event.persisted) { client.remember(null); schedule(); } });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') client.remember(null); else schedule(); });
    if (isCartPage()) schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();

